
import { useState, useEffect, useRef } from "react";

import { useScroll, useTransform } from "framer-motion";

function useHorizontalScrollAnimation() {
  const scrollContainerRef = useRef(null);
  const targetRef = useRef(null);
  const [scrollableWidth, setScrollableWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const { scrollYProgress } = useScroll({
    target: scrollContainerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    const calculateSizes = () => {
      const currentViewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      setViewportHeight(currentViewportHeight);

      const stickyElement = targetRef.current;
      const cardsWrapper = stickyElement?.querySelector(":scope > div:last-child"); // Get the cards container

      if (stickyElement && cardsWrapper) {
        const containerWidth = stickyElement.offsetWidth;
        const contentWidth = cardsWrapper.scrollWidth;
        const newScrollableWidth = contentWidth - containerWidth;
        setScrollableWidth(newScrollableWidth > 0 ? newScrollableWidth : 0);
      } else {
        setScrollableWidth(0);
      }
    };

    calculateSizes();
    window.addEventListener("resize", calculateSizes);
    return () => window.removeEventListener("resize", calculateSizes);
  }, []);

  // Define heights for pauses before and after the scroll (in pixels, using viewportHeight)
  const startPauseHeight = viewportHeight / 6; // Reduced pause for a quicker start
  const endPauseHeight = viewportHeight / 6; // Reduced pause for consistency

  // Total vertical scroll distance required for the entire animation (pauses + scroll itself)
  const totalScrollDistanceForAnimation =
    scrollableWidth + startPauseHeight + endPauseHeight;

  // Calculate the total height of the outer container
  // Height = distance for animation + initial viewport height (100vh) for correct offset calculation
  // Using pixels for more precise proportion calculations
  const scrollContainerHeight =
    totalScrollDistanceForAnimation + viewportHeight;

  // Calculate proportions for useTransform
  const startPauseProportion =
    totalScrollDistanceForAnimation > 0
      ? startPauseHeight / scrollContainerHeight
      : 0;
  const horizontalScrollEndProportion =
    totalScrollDistanceForAnimation > 0
      ? (startPauseHeight + scrollableWidth) / scrollContainerHeight
      : 0;

  // Input range for card scrolling
  const horizontalScrollInputRange = [
    startPauseProportion,
    horizontalScrollEndProportion,
  ];

  // Transform: scrollYProgress in this range -> x[0, -scrollableWidth]
  const x = useTransform(
    scrollYProgress,
    horizontalScrollInputRange, // Input range (middle part of the total progress)
    [0, -scrollableWidth], // Output range (horizontal offset)
    { clamp: true } // Clamp the output value
  );

  return {
    scrollContainerRef,
    targetRef,
    scrollContainerHeight,
    x,
  };
}

export default useHorizontalScrollAnimation;
